import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CommandCenterSidebar,
  SIDEBAR_WIDTH_CLASS,
} from '@/features/manager-command-center/components/CommandCenterSidebar';
import {
  EFFECTIVENESS_SUBLINE,
  EFFECTIVENESS_VERDICT,
  TEAM_LINKS,
  TEAM_MAP_NODES,
} from '../data/effectiveness-demo-dataset';
import type {
  EffectivenessTone,
  LinkSeverity,
  SummarySegment,
  TeamLink,
  TeamMapNode,
} from '../types/effectiveness';

/** Roomy report width - the hero map and the map+cards split both breathe. */
const PAGE_WIDTH = 'max-w-6xl';

/** Worst first. Drives both sort order and how loud each card reads. */
const RANK_ORDER: Record<LinkSeverity, number> = {
  critical: 0,
  elevated: 1,
  watch: 2,
  healthy: 3,
};

/**
 * Inline emphasis in the verdict sentence - color and weight only. Healthy reads
 * as quiet ink so the broken links carry the color.
 */
const HIGHLIGHT_TEXT: Record<EffectivenessTone, string> = {
  bad: 'font-semibold text-rose-600',
  warn: 'font-semibold text-amber-700',
  good: 'font-semibold text-emerald-700',
  neutral: 'font-semibold text-zinc-900',
};

/** Card chrome per severity - critical is heaviest, healthy reads calm. */
const CARD_SHELL: Record<LinkSeverity, string> = {
  critical:
    'border-rose-100 bg-white shadow-[0_28px_60px_-34px_rgba(244,63,94,0.45)]',
  elevated:
    'border-amber-100 bg-white shadow-[0_22px_54px_-34px_rgba(0,0,0,0.2)]',
  watch: 'border-amber-100 bg-white shadow-[0_22px_54px_-34px_rgba(0,0,0,0.2)]',
  healthy:
    'border-emerald-100 bg-white shadow-[0_18px_44px_-34px_rgba(16,185,129,0.4)]',
};

/** Top accent bar across each card - the loudest severity cue. */
const ACCENT_BAR: Record<LinkSeverity, string> = {
  critical: 'bg-rose-500',
  elevated: 'bg-amber-400',
  watch: 'bg-amber-400',
  healthy: 'bg-emerald-400',
};

/** Severity chip background + ink. */
const SEVERITY_CHIP: Record<LinkSeverity, string> = {
  critical: 'border-rose-100 bg-rose-50 text-rose-600',
  elevated: 'border-amber-100 bg-amber-50 text-amber-700',
  watch: 'border-amber-100 bg-amber-50 text-amber-700',
  healthy: 'border-emerald-100 bg-emerald-50 text-emerald-700',
};

/** Severity chip label. */
const SEVERITY_LABEL: Record<LinkSeverity, string> = {
  critical: 'Critical break',
  elevated: 'Elevated friction',
  watch: 'Watch',
  healthy: 'Flowing cleanly',
};

/** Cost-figure tint. */
const METRIC_VALUE_TEXT: Record<LinkSeverity, string> = {
  critical: 'text-rose-600',
  elevated: 'text-amber-600',
  watch: 'text-amber-600',
  healthy: 'text-emerald-600',
};

/** Caption under the cost figure. */
const METRIC_LABEL_TEXT: Record<LinkSeverity, string> = {
  critical: 'text-rose-500',
  elevated: 'text-amber-600/80',
  watch: 'text-amber-600/80',
  healthy: 'text-emerald-600/80',
};

/** Team-name ink + weight - healthy links fade so the eye skips them. */
const TEAM_NAME_TEXT: Record<LinkSeverity, string> = {
  critical: 'text-2xl font-bold text-zinc-900 md:text-3xl',
  elevated: 'text-2xl font-bold text-zinc-900 md:text-3xl',
  watch: 'text-xl font-semibold text-zinc-900',
  healthy: 'text-2xl font-bold text-zinc-500 md:text-3xl',
};

/** Handoff arrow tint between the two team names. */
const ARROW_TEXT: Record<LinkSeverity, string> = {
  critical: 'text-rose-400',
  elevated: 'text-amber-400',
  watch: 'text-amber-400',
  healthy: 'text-zinc-300',
};

/* ─────────────────────────── Dependency map ────────────────────────── */

/** Edge stroke on the map - neutral by default, hot only where it matters. */
const EDGE_STROKE: Record<LinkSeverity, string> = {
  critical: '#f43f5e',
  elevated: '#fb923c',
  watch: '#fbbf24',
  healthy: '#10b981',
};

/** Edge weight (non-scaling px) - thick where the dependency is breaking. */
const EDGE_WIDTH: Record<LinkSeverity, number> = {
  critical: 3.5,
  elevated: 2.5,
  watch: 2,
  healthy: 1.75,
};

/** Edge opacity - the broken links sit forward, the healthy ones recede. */
const EDGE_OPACITY: Record<LinkSeverity, number> = {
  critical: 0.9,
  elevated: 0.75,
  watch: 0.6,
  healthy: 0.55,
};

/** Node dot tint per severity. */
const NODE_DOT: Record<LinkSeverity, string> = {
  critical: 'bg-rose-500',
  elevated: 'bg-amber-500',
  watch: 'bg-amber-400',
  healthy: 'bg-emerald-500',
};

/** Node pill chrome - a faint tinted ring brands the teams on each link. */
const NODE_RING: Record<LinkSeverity, string> = {
  critical: 'border-rose-200 text-zinc-900',
  elevated: 'border-amber-200 text-zinc-900',
  watch: 'border-amber-200 text-zinc-800',
  healthy: 'border-emerald-200 text-zinc-800',
};

/** Fast lookup from a team name to its placed node. */
const NODE_BY_NAME: Record<string, TeamMapNode> = Object.fromEntries(
  TEAM_MAP_NODES.map((node) => [node.name, node])
);

/** Fast lookup from a link id to its link. */
const LINK_BY_ID: Record<string, TeamLink> = Object.fromEntries(
  TEAM_LINKS.map((link) => [link.id, link])
);

/** The worst severity each team carries across all the links it sits on. */
const NODE_WORST_SEVERITY: Record<string, LinkSeverity> = (() => {
  const worstByTeam: Record<string, LinkSeverity> = {};
  for (const link of TEAM_LINKS) {
    for (const teamName of [link.from, link.to]) {
      const current = worstByTeam[teamName];
      if (!current || RANK_ORDER[link.severity] < RANK_ORDER[current]) {
        worstByTeam[teamName] = link.severity;
      }
    }
  }
  return worstByTeam;
})();

/** Severity colors keyed for the arrowhead markers, plus a muted context tone. */
const ARROW_KEYS = [
  'critical',
  'elevated',
  'watch',
  'healthy',
  'muted',
] as const;
type ArrowKey = (typeof ARROW_KEYS)[number];

/** The muted slate used for context edges, matching the dependency-graph look. */
const MUTED_EDGE = '#cbd5e1';

interface Point {
  x: number;
  y: number;
}

/** Map a 0..100 window position to a pixel center within a measured box. */
function pointToPixel(point: Point, width: number, height: number): Point {
  return { x: (point.x / 100) * width, y: (point.y / 100) * height };
}

/** Build an SVG path through points with arc-rounded corners. */
function roundedPolyline(points: Point[], radius: number): string {
  if (points.length < 2) return '';
  const first = points[0];
  if (!first) return '';
  let path = `M ${first.x} ${first.y}`;
  for (let index = 1; index < points.length - 1; index += 1) {
    const previous = points[index - 1];
    const corner = points[index];
    const next = points[index + 1];
    if (!previous || !corner || !next) continue;
    const inLength =
      Math.hypot(corner.x - previous.x, corner.y - previous.y) || 1;
    const outLength = Math.hypot(next.x - corner.x, next.y - corner.y) || 1;
    const inRadius = Math.min(radius, inLength / 2);
    const outRadius = Math.min(radius, outLength / 2);
    const beforeCorner = {
      x: corner.x + ((previous.x - corner.x) / inLength) * inRadius,
      y: corner.y + ((previous.y - corner.y) / inLength) * inRadius,
    };
    const afterCorner = {
      x: corner.x + ((next.x - corner.x) / outLength) * outRadius,
      y: corner.y + ((next.y - corner.y) / outLength) * outRadius,
    };
    path += ` L ${beforeCorner.x} ${beforeCorner.y} Q ${corner.x} ${corner.y} ${afterCorner.x} ${afterCorner.y}`;
  }
  const last = points[points.length - 1];
  if (last) path += ` L ${last.x} ${last.y}`;
  return path;
}

/**
 * A right-angled "smoothstep" route between two node centers with rounded
 * corners, mirroring the dependency-graph edges: leave the source, turn at the
 * midline, and arrive square at the target. The arrival point is pulled back to
 * the edge of the target card so the arrowhead lands just outside it.
 */
function smoothstepPath(
  sourceNorm: Point,
  targetNorm: Point,
  width: number,
  height: number
): string {
  // Decide which way the edge turns in normalized 0..100 space, NOT in pixels,
  // so the same handoff routes identically whatever the box aspect ratio is
  // (the wide hero and the compact globe must agree on every turn).
  const horizontalDominant =
    Math.abs(targetNorm.x - sourceNorm.x) >
    Math.abs(targetNorm.y - sourceNorm.y);

  const source = pointToPixel(sourceNorm, width, height);
  const target = pointToPixel(targetNorm, width, height);
  const deltaX = target.x - source.x;
  const deltaY = target.y - source.y;

  // Pull the arrival back to the edge of the target card (constant px so the
  // arrowhead lands just outside the node at any map size).
  const endInsetX = horizontalDominant ? 60 : 0;
  const endInsetY = horizontalDominant ? 0 : 22;
  const end = {
    x: target.x - Math.sign(deltaX) * endInsetX,
    y: target.y - Math.sign(deltaY) * endInsetY,
  };

  const midX = (source.x + end.x) / 2;
  const midY = (source.y + end.y) / 2;
  const points: Point[] = horizontalDominant
    ? [source, { x: midX, y: source.y }, { x: midX, y: end.y }, end]
    : [source, { x: source.x, y: midY }, { x: end.x, y: midY }, end];

  return roundedPolyline(points, 14);
}

/**
 * The company connection map. In the hero (no active link) every handoff is
 * live and colored by severity, so a glance shows the whole org and where it
 * runs hot. In the sticky side variant one link is active: its edge and two
 * teams light up while the rest of the org recedes to context, so each use case
 * below points at exactly where its break sits on the same map.
 */
function DependencyMap({
  activeLinkId,
  variant,
  expanded = false,
}: {
  activeLinkId: string | null;
  variant: 'hero' | 'side';
  /** Side globe only: when expanded, drop the focus zoom and show the whole org. */
  expanded?: boolean;
}) {
  const activeLink = activeLinkId ? LINK_BY_ID[activeLinkId] : null;
  // Hero owns a fixed height; the side variant fills whatever the floating
  // globe sizes it to (collapsed small, expanded large on hover).
  const heightClass = variant === 'hero' ? 'h-[280px] md:h-[340px]' : 'h-full';
  const labelText =
    variant === 'hero' ? 'text-[11px] md:text-xs' : 'text-[10px]';
  const frameClass =
    variant === 'hero' ? 'rounded-2xl border border-zinc-100' : '';

  // Edges are drawn in pixel space so the smoothstep corners and arrowheads
  // stay undistorted as the box resizes (hero, collapsed globe, expanded globe).
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const hasSize = size.width > 0 && size.height > 0;

  // In the collapsed side globe a single link is active, so pan+zoom the map to
  // frame the active pair: its two teams and their edge land centered, while the
  // rest of the org drifts to the muted margins. When the globe is expanded (or
  // in the hero) we keep the identity view so the whole org reads at once.
  const focus = useMemo(() => {
    if (variant !== 'side' || !activeLink || expanded) return null;
    const fromNode = NODE_BY_NAME[activeLink.from];
    const toNode = NODE_BY_NAME[activeLink.to];
    if (!fromNode || !toNode) return null;
    const centerX = (fromNode.x + toNode.x) / 2;
    const centerY = (fromNode.y + toNode.y) / 2;
    // Span the pair covers on the busier axis; clamp so a tight pair does not
    // over-zoom and a wide pair still leaves room for the node cards.
    const pairSpan = Math.max(
      Math.abs(fromNode.x - toNode.x),
      Math.abs(fromNode.y - toNode.y),
      14
    );
    const zoom = Math.min(2.3, Math.max(1, 38 / pairSpan));
    return { centerX, centerY, zoom };
  }, [variant, activeLink, expanded]);

  // Each team's position in the displayed window: the focus transform when one
  // is active, otherwise the team's own 0..100 coordinate.
  const projectedByName = useMemo(() => {
    const result: Record<string, Point> = {};
    for (const node of TEAM_MAP_NODES) {
      result[node.name] = focus
        ? {
            x: (node.x - focus.centerX) * focus.zoom + 50,
            y: (node.y - focus.centerY) * focus.zoom + 50,
          }
        : { x: node.x, y: node.y };
    }
    return result;
  }, [focus]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full overflow-hidden bg-[#fcfcfd] ${heightClass} ${frameClass}`}
    >
      {/* Faint dot grid - the dependency-canvas backdrop */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgba(24,24,27,0.05) 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }}
      />

      {/* Edges - orthogonal smoothstep with arrowheads, lit by severity */}
      {hasSize && (
        <svg
          className="absolute inset-0 h-full w-full"
          viewBox={`0 0 ${size.width} ${size.height}`}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <defs>
            {ARROW_KEYS.map((arrowKey) => (
              <marker
                key={arrowKey}
                id={`eff-arrow-${variant}-${arrowKey}`}
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto"
              >
                <path
                  d="M 0 0 L 10 5 L 0 10 z"
                  fill={
                    arrowKey === 'muted' ? MUTED_EDGE : EDGE_STROKE[arrowKey]
                  }
                />
              </marker>
            ))}
          </defs>
          {TEAM_LINKS.map((link) => {
            const from = projectedByName[link.from];
            const to = projectedByName[link.to];
            if (!from || !to) return null;
            const isLit = !activeLink || link.id === activeLink.id;
            const arrowKey: ArrowKey = isLit ? link.severity : 'muted';
            const isCritical = isLit && link.severity === 'critical';
            return (
              <path
                key={link.id}
                d={smoothstepPath(from, to, size.width, size.height)}
                fill="none"
                stroke={isLit ? EDGE_STROKE[link.severity] : MUTED_EDGE}
                strokeWidth={isLit ? EDGE_WIDTH[link.severity] : 1.25}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={isCritical ? '6 5' : undefined}
                opacity={isLit ? EDGE_OPACITY[link.severity] : 0.4}
                markerEnd={`url(#eff-arrow-${variant}-${arrowKey})`}
                className={isCritical ? 'animate-pulse' : undefined}
              />
            );
          })}
        </svg>
      )}

      {/* Nodes - dependency-style cards; context teams recede to muted cards */}
      {TEAM_MAP_NODES.map((node) => {
        const isEmphasized = activeLink
          ? node.name === activeLink.from || node.name === activeLink.to
          : true;
        const severity = activeLink
          ? activeLink.severity
          : NODE_WORST_SEVERITY[node.name] ?? 'healthy';
        const placement = projectedByName[node.name] ?? {
          x: node.x,
          y: node.y,
        };

        if (!isEmphasized) {
          return (
            <span
              key={node.name}
              aria-hidden="true"
              className="absolute z-0 inline-flex max-w-[60%] -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 truncate rounded-lg border border-zinc-100 bg-white/90 px-2 py-1 text-[10px] font-medium text-zinc-400 shadow-sm rtl:translate-x-1/2"
              style={{
                insetInlineStart: `${placement.x}%`,
                top: `${placement.y}%`,
              }}
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-300 ring-2 ring-white" />
              {node.name}
            </span>
          );
        }

        return (
          <span
            key={node.name}
            className={`absolute z-10 inline-flex max-w-[64%] -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 truncate rounded-lg border bg-white px-2.5 py-1 font-semibold shadow-sm rtl:translate-x-1/2 ${labelText} ${NODE_RING[severity]}`}
            style={{
              insetInlineStart: `${placement.x}%`,
              top: `${placement.y}%`,
            }}
          >
            <span
              className={`h-2 w-2 shrink-0 rounded-full ring-2 ring-white ${
                severity === 'critical' ? 'animate-pulse' : ''
              } ${NODE_DOT[severity]}`}
            />
            {node.name}
          </span>
        );
      })}
    </div>
  );
}

/** Edge key for the map - line + arrowhead previews, like the dependency graph. */
const LEGEND_ITEMS: { key: ArrowKey; label: string; dash?: string }[] = [
  { key: 'critical', label: 'Critical', dash: '4 3' },
  { key: 'elevated', label: 'Elevated' },
  { key: 'healthy', label: 'Healthy' },
];

function MapLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] font-medium text-zinc-500">
      {LEGEND_ITEMS.map((item) => {
        const color = EDGE_STROKE[item.key as LinkSeverity];
        return (
          <span key={item.key} className="inline-flex items-center gap-1.5">
            <svg width="26" height="10" aria-hidden="true" className="shrink-0">
              <defs>
                <marker
                  id={`legend-arrow-${item.key}`}
                  viewBox="0 0 10 10"
                  refX="7"
                  refY="5"
                  markerWidth="5"
                  markerHeight="5"
                  orient="auto"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
                </marker>
              </defs>
              <line
                x1="1"
                y1="5"
                x2="19"
                y2="5"
                stroke={color}
                strokeWidth="2"
                strokeDasharray={item.dash}
                markerEnd={`url(#legend-arrow-${item.key})`}
              />
            </svg>
            {item.label}
          </span>
        );
      })}
    </div>
  );
}

/* ─────────────────────────── Floating globe ───────────────────────── */

/**
 * A small company map docked in the bottom-right corner. It stays out of the
 * way while reading and lights up whichever handoff belongs to the case in
 * view; hovering expands it into a full, legible map with the active-link
 * caption and legend. Hidden below lg where the corner would crowd the cards.
 */
function FloatingGlobe({ activeLink }: { activeLink: TeamLink | undefined }) {
  // Collapsed, the globe zooms to the active link; expanded, it shows the whole
  // org (still lighting that link). Track hover so the map knows which to render.
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className="group fixed bottom-6 end-6 z-50 hidden lg:block"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <div className="w-[240px] overflow-hidden rounded-2xl border border-zinc-200 bg-white/95 shadow-[0_24px_60px_-24px_rgba(15,23,42,0.45)] backdrop-blur transition-[width] duration-300 ease-out group-hover:w-[480px]">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 border-b border-zinc-100 px-3 py-2">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-500">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#00AEEF]" />
            Live topology
          </span>
          <span className="text-[10px] font-medium text-zinc-400 transition-opacity duration-200 group-hover:opacity-0">
            hover to expand
          </span>
        </div>

        {/* Map - grows on hover */}
        <div className="h-[160px] transition-[height] duration-300 ease-out group-hover:h-[320px]">
          <DependencyMap
            activeLinkId={activeLink?.id ?? null}
            variant="side"
            expanded={isExpanded}
          />
        </div>

        {/* Active-link caption + legend - revealed on hover */}
        <div className="max-h-0 overflow-hidden opacity-0 transition-all duration-300 ease-out group-hover:max-h-48 group-hover:opacity-100">
          <div className="space-y-3 border-t border-zinc-100 p-3">
            {activeLink && (
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${
                    NODE_DOT[activeLink.severity]
                  }`}
                />
                <span className="text-sm font-semibold text-zinc-900">
                  {activeLink.from}
                </span>
                <HandoffArrow
                  severity={activeLink.severity}
                  className="h-4 w-4"
                />
                <span className="text-sm font-semibold text-zinc-900">
                  {activeLink.to}
                </span>
                {activeLink.metricLabel && (
                  <span
                    className={`ms-auto text-xs font-semibold ${
                      METRIC_VALUE_TEXT[activeLink.severity]
                    }`}
                  >
                    {activeLink.metricValue}
                  </span>
                )}
              </div>
            )}
            <MapLegend />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Shared bits ──────────────────────────── */

function SummarySentence({
  segments,
  className,
}: {
  segments: SummarySegment[];
  className?: string;
}) {
  return (
    <p className={className}>
      {segments.map((segment, index) =>
        segment.highlight ? (
          <span key={index} className={HIGHLIGHT_TEXT[segment.highlight]}>
            {segment.text}
          </span>
        ) : (
          <span key={index}>{segment.text}</span>
        )
      )}
    </p>
  );
}

function HandoffArrow({
  severity,
  className,
}: {
  severity: LinkSeverity;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={`shrink-0 rtl:rotate-180 ${ARROW_TEXT[severity]} ${
        className ?? 'h-7 w-7'
      }`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function SeverityIcon({ severity }: { severity: LinkSeverity }) {
  if (severity === 'healthy') {
    return (
      <svg
        viewBox="0 0 24 24"
        className="h-4 w-4"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.1 14.2-3.6-3.6 1.4-1.4 2.2 2.2 4.8-4.8 1.4 1.4-6.2 6.2z" />
      </svg>
    );
  }
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2 1 21h22L12 2zm1 14h-2v2h2v-2zm0-6h-2v4h2v-4z" />
    </svg>
  );
}

/* ───────────────────────────── Case card ──────────────────────────── */

function LinkCard({
  link,
  index,
  registerRef,
}: {
  link: TeamLink;
  index: number;
  registerRef: (element: HTMLElement | null) => void;
}) {
  const isHealthy = link.severity === 'healthy';

  return (
    <article
      ref={registerRef}
      data-link-id={link.id}
      className={`relative overflow-hidden rounded-3xl border p-6 md:p-9 ${
        CARD_SHELL[link.severity]
      }`}
    >
      {/* Top accent bar */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-x-0 top-0 h-1.5 ${
          ACCENT_BAR[link.severity]
        }`}
      />

      {/* Header meta */}
      <div className="flex items-center justify-between gap-3">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-wide ${
            SEVERITY_CHIP[link.severity]
          }`}
        >
          <SeverityIcon severity={link.severity} />
          {SEVERITY_LABEL[link.severity]}
        </span>
        <span className="text-xs font-medium text-zinc-400">
          {isHealthy ? `Case ${link.caseId}` : `Case #${link.caseId}`}
        </span>
      </div>

      {/* Team to team */}
      <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="flex h-7 min-w-7 items-center justify-center rounded-md bg-zinc-900 px-1.5 text-xs font-bold text-white">
          {index}
        </span>
        <span className={TEAM_NAME_TEXT[link.severity]}>{link.from}</span>
        <HandoffArrow severity={link.severity} />
        <span className={TEAM_NAME_TEXT[link.severity]}>{link.to}</span>
        {link.eyebrow && (
          <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-rose-600">
            {link.eyebrow}
          </span>
        )}
      </div>

      {/* Plain-language reason */}
      <p
        className={`mt-5 text-lg leading-snug md:text-xl ${
          isHealthy ? 'font-medium text-zinc-500' : 'font-medium text-zinc-700'
        }`}
      >
        {link.reason}
      </p>

      {/* Mechanism detail */}
      {link.detail && (
        <p
          className={`mt-3 text-sm leading-relaxed ${
            isHealthy ? 'text-zinc-400' : 'text-zinc-600'
          }`}
        >
          {link.detail}
        </p>
      )}

      {/* Dominant cost + cited incident (broken links only) */}
      {!isHealthy && (
        <div className="mt-6 flex flex-col gap-6 rounded-2xl border border-zinc-100 bg-zinc-50/70 p-6 md:flex-row md:items-center">
          <div className="md:min-w-[230px]">
            <span className="block text-xs font-semibold uppercase tracking-wider text-zinc-500">
              Dominant cost
            </span>
            <div className="mt-2 flex items-baseline gap-2">
              <span
                className={`text-4xl font-bold tracking-tight md:text-5xl ${
                  METRIC_VALUE_TEXT[link.severity]
                }`}
              >
                {link.metricValue}
              </span>
            </div>
            <span
              className={`mt-1 block text-xs font-semibold uppercase tracking-wider ${
                METRIC_LABEL_TEXT[link.severity]
              }`}
            >
              {link.metricLabel}
            </span>
          </div>
          {link.example && (
            <>
              <span
                aria-hidden="true"
                className="hidden w-px self-stretch bg-zinc-200 md:block"
              />
              <div className="min-w-0">
                <span className="block text-xs font-semibold uppercase tracking-wider text-zinc-500">
                  Cited example
                </span>
                {link.incidentRef && (
                  <div className="mt-2 text-sm font-semibold text-zinc-900">
                    {link.incidentRef}
                  </div>
                )}
                <p className="mt-1 text-sm leading-relaxed text-zinc-600">
                  {link.example}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Supporting signals */}
      {link.signals && link.signals.length > 0 && (
        <ul className="mt-6 grid gap-2.5">
          {link.signals.map((signal) => (
            <li
              key={signal}
              className="flex items-start gap-2.5 text-sm text-zinc-600"
            >
              <span
                className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                  NODE_DOT[link.severity]
                }`}
              />
              <span>{signal}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Supporting stats strip */}
      {link.secondaryStats && link.secondaryStats.length > 0 && (
        <div className="mt-6 grid grid-cols-3 gap-px overflow-hidden rounded-xl bg-zinc-100">
          {link.secondaryStats.map((stat) => (
            <div key={stat.label} className="bg-white p-4">
              <div className="text-2xl font-bold tracking-tight text-zinc-900">
                {stat.value}
              </div>
              <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recommended move */}
      {link.recommendation && (
        <div className="mt-6 flex flex-col gap-4 border-t border-zinc-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h4 className="text-sm font-bold text-zinc-900">
              {isHealthy ? 'Keep it' : 'Recommended move'}
            </h4>
            <p className="mt-1 text-sm leading-relaxed text-zinc-500">
              {link.recommendation}
            </p>
          </div>
          {!isHealthy && (
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-zinc-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-zinc-700 sm:self-auto"
            >
              Draft the fix
              <HandoffArrow severity={link.severity} className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </article>
  );
}

/* ───────────────────────────── Page ───────────────────────────────── */

/**
 * The effectiveness view - a manager's at-a-glance read of which teams under
 * them are badly connected, and why. A full company connection map anchors the
 * top; scrolling shrinks it into a sticky side legend that lights up the single
 * handoff belonging to the case currently in view. The cases themselves are a
 * worst-first run of large, evidence-rich cards: the team pair, the reason, a
 * cited example, supporting signals and stats, the one cost number that hurts,
 * and the recommended move.
 */
export function EffectivenessPage() {
  const rankedLinks = useMemo(
    () =>
      [...TEAM_LINKS].sort(
        (first, second) =>
          RANK_ORDER[first.severity] - RANK_ORDER[second.severity]
      ),
    []
  );
  const criticalCount = useMemo(
    () => TEAM_LINKS.filter((link) => link.severity === 'critical').length,
    []
  );

  const [activeLinkId, setActiveLinkId] = useState<string>(
    rankedLinks[0]?.id ?? ''
  );
  const cardRefs = useRef<Map<string, HTMLElement>>(new Map());
  const visibleIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const linkId = (entry.target as HTMLElement).dataset.linkId;
          if (!linkId) continue;
          if (entry.isIntersecting) {
            visibleIds.current.add(linkId);
          } else {
            visibleIds.current.delete(linkId);
          }
        }
        const firstVisible = rankedLinks.find((link) =>
          visibleIds.current.has(link.id)
        );
        if (firstVisible) {
          setActiveLinkId(firstVisible.id);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: 0 }
    );

    cardRefs.current.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [rankedLinks]);

  const activeLink = LINK_BY_ID[activeLinkId];

  return (
    <div className="relative min-h-[100dvh] bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_60%,#f3f6fb_100%)] text-black">
      <div className="pointer-events-none absolute inset-x-0 -top-24 h-72 bg-[radial-gradient(58%_100%_at_50%_0%,rgba(0,174,239,0.07),transparent_72%)]" />

      <CommandCenterSidebar activeTab="effectiveness" />

      <div className={SIDEBAR_WIDTH_CLASS}>
        <main className="relative z-10 w-full px-5 pb-28 pt-8 md:px-10">
          <div className={`mx-auto w-full ${PAGE_WIDTH}`}>
            {/* Verdict */}
            <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-400">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
              Effectiveness · team links
            </p>
            <SummarySentence
              segments={EFFECTIVENESS_VERDICT}
              className="mt-4 max-w-3xl text-2xl font-normal leading-snug tracking-tight text-zinc-800 md:text-[30px]"
            />
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-500">
              {EFFECTIVENESS_SUBLINE}
            </p>
            <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-rose-100 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
              {criticalCount} critical links need you this week
            </p>

            {/* Hero - the full company connection map */}
            <section className="mt-8">
              <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-900">
                    The full picture
                  </h2>
                  <p className="mt-1 max-w-xl text-sm text-zinc-500">
                    Every team you own and how their work flows to the next. The
                    hot links are where it breaks - scroll to walk each one.
                  </p>
                </div>
                <MapLegend />
              </div>
              <DependencyMap activeLinkId={null} variant="hero" />
            </section>

            {/* Full-width worst-first cases - the corner globe tracks them */}
            <div className="mt-12 space-y-10">
              {rankedLinks.map((link, rankIndex) => (
                <LinkCard
                  key={link.id}
                  link={link}
                  index={rankIndex + 1}
                  registerRef={(element) => {
                    if (element) {
                      cardRefs.current.set(link.id, element);
                    } else {
                      cardRefs.current.delete(link.id);
                    }
                  }}
                />
              ))}

              {/* One restrained action */}
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#0F2F5F] via-[#1678C2] to-[#22D1C5] px-7 py-3 text-sm font-semibold text-white shadow-[0_12px_30px_-10px_rgba(22,120,194,0.6)] transition-transform hover:scale-[1.02] active:scale-95"
                >
                  Open the alignment plan
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4 rtl:rotate-180"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Corner globe - small by default, expands on hover */}
      <FloatingGlobe activeLink={activeLink} />
    </div>
  );
}
